import type { LucideIcon } from "lucide-react";
export type CommandSource = "palette" | "keyboard" | "chat" | "voice";
export interface CommandContext { source: CommandSource }
/** Groups the palette knows by name and order. */
export type KnownCommandGroupId = "story" | "history" | "assistant" | "navigation" | "help";
/** A known group, or any new one a caller registers (listed after the known ones, label from `commandGroupLabel`). */
export type CommandGroupId = KnownCommandGroupId | (string & {});
export type CommandAvailability =
  | { status: "ready" } | { status: "soon" } | { status: "blocked"; reason: string } | { status: "hidden" };
/** `mod`: Cmd on macOS, Ctrl elsewhere. `key`: KeyboardEvent.key, lower case ("k", "?", "/"). */
export interface KeyChord { key: string; mod?: boolean; shift?: boolean; alt?: boolean; ctrl?: boolean }
export interface CommandShortcut {
  chords: KeyChord[];
  /** The chords are keys pressed one after another ("G" then "C"), not alternatives. Bare keys only. */
  sequence?: boolean;
  /** Fires inside fields too (only Mod+K tonight). */ allowInFields?: boolean;
  /** Bound by someone else (S7 undo/redo): listed and shown, never bound here. */ boundElsewhere?: boolean;
}
export type CommandArgSpec = { name: string; description: string; required?: boolean } & (
  | { kind: "entity"; entity: string; options: () => { id: string; label: string }[] }
  | { kind: "number"; min?: number; max?: number; default?: number }
  | { kind: "text" });
export type CommandArgs = Record<string, string | number | undefined>;
export interface CommandResult { status: "done" | "cancelled" | "blocked" | "failed"; message: string }
/** A glyph component the palette renders at a fixed size (the app draws its icons with Lucide). */
export type CommandIcon = LucideIcon;
export interface CommandAction {
  id: string;                 // "history.undo", "story.editChart"
  label: string;              // palette row, sentence case
  group: CommandGroupId;
  keywords?: string[];
  /** Palette row glyph (16px). */
  icon?: CommandIcon;
  /** Palette row note on the right, e.g. a current value or what it produces. */
  hint?: string;
  /** Whole-message typed/spoken forms; `{arg}` names an arg: "undo last {count}", "edit {chart}". */
  patterns?: string[];
  shortcut?: CommandShortcut;
  args?: CommandArgSpec[];
  /** Always asks first (default copy). */ destructive?: boolean;
  /** Asks per run when it returns copy (e.g. undo of several steps). */
  confirm?: (args: CommandArgs) => { title: string; body: string } | null;
  /** The history store already logs it: the activity feed skips its `ran` event. */ inHistory?: boolean;
  /** Never matched from chat or voice. */ paletteOnly?: boolean;
  isAvailable: (ctx: CommandContext) => CommandAvailability;
  run: (args: CommandArgs, ctx: CommandContext) => Promise<CommandResult> | CommandResult;
}
export interface CommandIntent { actionId: string; args: CommandArgs; via: "rule" | "model" }
export type CommandResolution =
  | { kind: "intent"; intent: CommandIntent } | { kind: "ambiguous"; message: string } | { kind: "none" };
/** Rules tonight; an LLM tool-calling resolver appends later (its tools come from `toToolDefinitions`). */
export interface CommandResolver {
  id: string;
  resolve: (text: string, actions: readonly CommandAction[], ctx: CommandContext) => Promise<CommandResolution>;
}
export interface CommandEvent { kind: "ran"; action: CommandAction; args: CommandArgs; ctx: CommandContext; result: CommandResult; at: number }
export interface CommandRegistryApi {
  /** `source` is read on every use, so actions are always the caller's latest. Last registered id wins. */
  register: (source: () => CommandAction[]) => () => void;
  list: () => CommandAction[];
  get: (id: string) => CommandAction | undefined;
  /** Availability → confirmation → run → emit `ran`. Never throws (errors → `failed`). */
  run: (id: string, args: CommandArgs, ctx: CommandContext) => Promise<CommandResult>;
  /** Resolvers in order; the first non-`none` wins. Skips `paletteOnly` for chat/voice. */
  resolve: (text: string, ctx: CommandContext) => Promise<CommandResolution>;
  subscribe: (listener: (event: CommandEvent) => void) => () => void;
  ui: { paletteOpen: boolean; shortcutsOpen: boolean; setPaletteOpen: (open: boolean) => void; setShortcutsOpen: (open: boolean) => void };
}
export interface ChatCommandReply { variant: "success" | "info" | "error"; title: string; description?: string; bubble?: "undo" }
/** `null`: not a command, send to the AI chat as today. */
export type ChatCommandHandler = (text: string, source: "chat" | "voice") => Promise<ChatCommandReply | null>;
export const isMacPlatform = (): boolean => /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);
